"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { GroqImage } from "@/helpers/Image";
import { SuccessStoryCard } from "@/sanity/pages/PageSuccessStoriesOverviewTemplate";
import { resolveLink } from "@/sanity/globals/CTA";

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "2-digit",
  });
}

export default function ArticleCard({
  article,
  language,
}: {
  article: SuccessStoryCard;
  language: string;
}) {
  const { titleText, excerpt, mainImage, date, category, slug } = article;

  const link = slug ? resolveLink(slug || "/", language) : "#";

  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  return (
    <Link
      href={finalLink}
      className="group h-full flex flex-col border border-black/20 rounded-[0.25rem] bg-white"
    >
      <div className="px-[1.5rem] pt-[1.5rem] md:px-[2.375rem] md:pt-[2.375rem] flex-shrink-0">
        <div className="relative w-full aspect-video overflow-hidden">
          {mainImage?.src ? (
            <GroqImage
              src={mainImage.src}
              alt={mainImage.name || ""}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="w-full h-full bg-[#f0f0f0]" />
          )}
          {category && (
            <span className="absolute top-[1.5rem] left-[1.5rem] md:top-[2.25rem] md:left-[2rem] bg-[#0b6f53] text-white text-[1.5rem] md:text-[1.75rem] leading-[1.3] tracking-[-0.05rem] px-[1rem] py-[0.5rem] md:px-[1.25rem] md:py-[0.625rem] rounded-[0.25rem]">
              {category}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-[2.5rem] px-[2.5rem] pt-[2.5rem] pb-[3rem] md:gap-[3.375rem] md:px-[4.375rem] md:pt-[3.375rem] md:pb-[4.375rem] flex-1">
        <div className="flex flex-col gap-[1.75rem]">
          <div className="flex flex-col gap-[1rem]">
            {date && (
              <p className="mb-0 text-[2rem] leading-[1.3] tracking-[-0.06rem] text-black opacity-50">
                {formatDate(date)}
              </p>
            )}
            {titleText && (
              <p className="mb-0 text-[2.5rem] md:text-[3rem] font-medium leading-[1.3] tracking-[-0.09rem] text-black">
                {titleText}
              </p>
            )}
          </div>
          {excerpt && (
            <p className="mb-0 text-[2rem] leading-[1.5] text-black opacity-70 line-clamp-3">
              {excerpt}
            </p>
          )}
        </div>
        <div className="flex gap-[0.875rem] items-center mt-auto">
          <p className="mb-0 text-[2rem] leading-normal tracking-[-0.08rem] text-black">
            Read more
          </p>
          <svg
            viewBox="0 0 7.16381 10.4866"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="flex-shrink-0 w-[0.625rem] h-[1.125rem]"
          >
            <path
              d="M0.668965 0.743294L5.66896 5.24329L0.668965 9.74329"
              stroke="#0B6F53"
              strokeWidth="2"
            />
          </svg>
        </div>
      </div>
    </Link>
  );
}
